import React, { useEffect, useRef, useState, useMemo } from 'react';

const DEFAULT_PROMPT = 'C:> ';

const normalizeCommand = (command = '') => {
  if (typeof command !== 'string') {
    return '';
  }

  let normalized = command.trim().toLowerCase();

  // Remove surrounding quotes often used in commands
  normalized = normalized.replace(/["']/g, '');

  // Normalise slashes
  normalized = normalized.replace(/\//g, '\\');

  // Collapse duplicate backslashes
  normalized = normalized.replace(/\\{2,}/g, '\\');

  // Fix known content typos (Aderson -> Anderson)
  normalized = normalized.replace(/john aderson/g, 'john anderson');

  // Collapse whitespace
  normalized = normalized.replace(/\s+/g, ' ');

  return normalized;
};

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const splitOutput = (output) => {
  if (!output) {
    return [''];
  }
  if (Array.isArray(output)) {
    return output.flatMap((line) => (typeof line === 'string' ? line.split(/\r?\n/) : []));
  }
  return String(output).split(/\r?\n/);
};

const CommandPromptContent = ({
  activeStep,
  scenario,
  onExecuteCommand,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [history, setHistory] = useState([
    { id: 'banner-1', type: 'output', text: 'Microsoft Windows [Version 11.0.22631]' },
    { id: 'banner-2', type: 'output', text: '(c) Microsoft Corporation. All rights reserved.' },
    { id: 'banner-3', type: 'output', text: '' },
  ]);
  const [commandIndex, setCommandIndex] = useState(null);
  const executedCommandsRef = useRef([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const expectedMeta = useMemo(() => {
    // Safety check
    if (!activeStep || !activeStep.content) {
      console.log('[CommandPrompt] No activeStep or content:', { activeStep });
      return null;
    }

    // Support both 'run_command' action and 'toolCommand' field
    // Also support steps with zipContents discoveries that need commands to list ZIP
    const hasRunCommand = activeStep.content.action === 'run_command';
    const hasToolCommand = activeStep.content.toolCommand || activeStep.content.toolRun;
    const hasZipContents = activeStep.content?.discoveries?.zipContents && Array.isArray(activeStep.content.discoveries.zipContents);

    console.log('[CommandPrompt] Checking step content:', {
      hasRunCommand,
      hasToolCommand,
      hasZipContents,
      toolCommand: activeStep.content.toolCommand,
      toolRun: activeStep.content.toolRun,
      action: activeStep.content.action,
      content: activeStep.content
    });

    if (!hasRunCommand && !hasToolCommand && !hasZipContents) {
      console.log('[CommandPrompt] No command action found in step');
      return null;
    }

    let expectedCommands = [];
    if (hasRunCommand) {
      expectedCommands = toArray(activeStep.content.command);
    } else if (hasToolCommand) {
      // Extract command from toolCommand field
      const toolCmd = activeStep.content.toolCommand || '';
      if (toolCmd) {
        expectedCommands = [toolCmd];
      }
    } else if (hasZipContents) {
      // For steps with zipContents, accept common ZIP listing commands
      // Look for ZIP file in fileSystem or use default name
      const zipFileName = activeStep.content?.fileSystem?.[0]?.name ||
        activeStep.content?.destination?.split(/[\\/]/).pop() ||
        'Invoice_7823.zip';
      expectedCommands = [
        `unzip -l ${zipFileName}`,
        `unzip -l "C:\\Users\\test.user\\Desktop\\${zipFileName}"`,
        `7z l ${zipFileName}`,
        `7z l "C:\\Users\\test.user\\Desktop\\${zipFileName}"`
      ];
    }

    // Build expected output from various sources (prioritize expectedOutputPreview)
    let expectedOutput = activeStep.content.expectedOutputPreview ||
      activeStep.content.expected_output ||
      activeStep.content.expectedOutput;

    // If we have discoveries, build a summary output
    if (!expectedOutput && activeStep?.content?.discoveries) {
      const discoveries = activeStep.content.discoveries;
      const outputLines = [];

      if (discoveries.macroStreams && discoveries.macroStreams.length > 0) {
        discoveries.macroStreams.forEach(stream => {
          outputLines.push(`Stream: ${stream.streamName || 'VBAProject'}`);
          if (stream.contentPreview) {
            outputLines.push(stream.contentPreview);
          }
          if (stream.embeddedBehavior) {
            outputLines.push(`Behavior: ${stream.embeddedBehavior}`);
          }
        });
      }

      if (discoveries.ioc) {
        const ioc = discoveries.ioc;
        if (ioc.url) outputLines.push(`URL: ${ioc.url}`);
        if (ioc.payload_filename) outputLines.push(`Payload: ${ioc.payload_filename}`);
        if (ioc.suspicious_patterns && ioc.suspicious_patterns.length > 0) {
          outputLines.push(`Suspicious patterns: ${ioc.suspicious_patterns.join(', ')}`);
        }
      }

      // Handle ADS (Alternate Data Stream) discoveries
      if (discoveries.adsPath || discoveries.adsSize || discoveries.adsPayload) {
        if (discoveries.adsPath) {
          // Format: notes.txt:secret  2,097,152 bytes (matching dir /r output format)
          const adsName = discoveries.adsPath.split(':').pop() || 'unknown';
          const parentFile = discoveries.adsPath.split(':')[0] || 'unknown';
          const sizeBytes = discoveries.adsSize ?
            (discoveries.adsSize.includes('MB') ?
              parseFloat(discoveries.adsSize) * 1024 * 1024 :
              parseFloat(discoveries.adsSize.replace(/[^\d.]/g, '')) * 1024) :
            2097152; // Default 2MB in bytes
          const formattedSize = sizeBytes.toLocaleString();
          outputLines.push(`${parentFile}:${adsName}  ${formattedSize} bytes`);
        }
        if (discoveries.adsPayload) {
          outputLines.push(`ADS Payload: ${discoveries.adsPayload}`);
        }
      }

      // Handle ZIP contents discoveries
      if (discoveries.zipContents && Array.isArray(discoveries.zipContents)) {
        // Format similar to unzip -l output
        outputLines.push('Archive:  Invoice_7823.zip');
        outputLines.push('Length      Date    Time    Name');
        outputLines.push('---------  ---------- -----   ----');

        discoveries.zipContents.forEach(item => {
          const size = item.size || '0';
          const name = item.name || 'unknown';
          const note = item.note ? `  [${item.note}]` : '';
          // Format: size (right-aligned), date, time, filename
          outputLines.push(`${size.padStart(9)}  2025-12-01 12:00   ${name}${note}`);
        });

        // Add summary line
        const totalSize = discoveries.zipContents.reduce((sum, item) => {
          const sizeStr = item.size || '0';
          const sizeNum = parseInt(sizeStr.replace(/[^\d]/g, '')) || 0;
          return sum + sizeNum;
        }, 0);
        outputLines.push(`---------                     -------`);
        outputLines.push(`${totalSize.toString().padStart(9)}                    ${discoveries.zipContents.length} file${discoveries.zipContents.length !== 1 ? 's' : ''}`);
      }

      if (outputLines.length > 0) {
        expectedOutput = outputLines.join('\n');
      }
    }

    return {
      commands: expectedCommands.filter(Boolean),
      expectedOutput: expectedOutput,
      discoveries: activeStep.content.discoveries,
      onDiscovery: activeStep.content.onDiscovery,
    };
  }, [activeStep]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const handleCommandSubmit = (e) => {
    e.preventDefault();
    const rawCommand = inputValue.trim();
    if (!rawCommand) {
      return;
    }

    const commandHistory = executedCommandsRef.current;
    if (!commandHistory.length || commandHistory[commandHistory.length - 1] !== rawCommand) {
      commandHistory.push(rawCommand);
    }
    setCommandIndex(null);

    const entries = [];
    entries.push({
      id: `cmd-${Date.now()}-${Math.random()}`,
      type: 'input',
      text: `${DEFAULT_PROMPT}${rawCommand}`,
    });

    let matchesExpected = false;
    let outputLines = [];

    if (expectedMeta?.commands?.length) {
      const normalizedInput = normalizeCommand(rawCommand);
      console.log('[CommandPrompt] Checking command:', {
        rawCommand,
        normalizedInput,
        expectedCommands: expectedMeta.commands
      });

      matchesExpected = expectedMeta.commands.some((cmd) => {
        const normalizedCmd = normalizeCommand(cmd);
        console.log('[CommandPrompt] Comparing:', { normalizedCmd, normalizedInput });

        // Exact match
        if (normalizedCmd === normalizedInput) {
          console.log('[CommandPrompt] Exact match found!');
          return true;
        }
        // Flexible match: check if the command starts with the expected tool name
        // This allows for path variations like "oledump Project-Plan.docm" vs "oledump C:\path\Project-Plan.docm"
        const cmdParts = normalizedCmd.split(' ');
        const inputParts = normalizedInput.split(' ');
        if (cmdParts.length > 0 && inputParts.length > 0 && cmdParts[0] === inputParts[0]) {
          // Same tool/command, check if filename matches (allow path variations)
          if (cmdParts.length === inputParts.length || cmdParts.length === 2) {
            const expectedFilename = cmdParts[cmdParts.length - 1].toLowerCase();
            const inputFilename = inputParts[inputParts.length - 1].toLowerCase();
            // Extract just the filename from path if needed
            const inputFileOnly = inputFilename.split('\\').pop() || inputFilename;
            const expectedFileOnly = expectedFilename.split('\\').pop() || expectedFilename;
            if (inputFileOnly === expectedFileOnly || inputFilename.includes(expectedFileOnly)) {
              console.log('[CommandPrompt] Flexible match found!');
              return true;
            }
          }
        }

        // Special handling for ZIP listing commands (unzip -l or 7z l)
        // Accept any ZIP listing command that references the ZIP file
        if ((normalizedCmd.includes('unzip') && normalizedCmd.includes('-l')) ||
          (normalizedCmd.includes('7z') && normalizedCmd.includes('l'))) {
          const inputLower = normalizedInput.toLowerCase();
          if ((inputLower.includes('unzip') && inputLower.includes('-l')) ||
            (inputLower.includes('7z') && (inputLower.includes(' l ') || inputLower.startsWith('7z l')))) {
            // Check if the command references a ZIP file (Invoice_7823.zip or similar)
            const zipFilePattern = /invoice.*\.zip|\.zip/i;
            if (zipFilePattern.test(normalizedInput) || zipFilePattern.test(normalizedCmd)) {
              console.log('[CommandPrompt] ZIP listing command match found!');
              return true;
            }
          }
        }
        return false;
      });

      console.log('[CommandPrompt] Final match result:', matchesExpected);
    } else {
      console.log('[CommandPrompt] No expected commands found in expectedMeta');
    }

    if (matchesExpected) {
      outputLines = splitOutput(expectedMeta?.expectedOutput);
    } else {
      outputLines = [`'${rawCommand}' is not recognized as part of the guided exercise.`];
    }

    outputLines.forEach((line) => {
      entries.push({
        id: `out-${Date.now()}-${Math.random()}`,
        type: 'output',
        text: line,
      });
    });

    setHistory((prev) => [...prev, ...entries]);
    setInputValue('');

    if (onExecuteCommand) {
      onExecuteCommand({
        command: rawCommand,
        matchesExpected,
        output: outputLines.join('\n'),
        outputLines,
        stepId: activeStep?.id,
        step: activeStep,
        discoveries: matchesExpected ? expectedMeta?.discoveries : undefined,
        onDiscovery: matchesExpected ? expectedMeta?.onDiscovery : undefined,
        scenario,
      });
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const navigateHistory = (direction) => {
    const commands = executedCommandsRef.current;
    if (!commands.length) {
      return;
    }

    if (direction === 'up') {
      const nextIndex = commandIndex === null ? commands.length - 1 : Math.max(0, commandIndex - 1);
      setCommandIndex(nextIndex);
      setInputValue(commands[nextIndex]);
    } else {
      if (commandIndex === null) {
        return;
      }
      const nextIndex = commandIndex + 1;
      if (nextIndex >= commands.length) {
        setCommandIndex(null);
        setInputValue('');
      } else {
        setCommandIndex(nextIndex);
        setInputValue(commands[nextIndex]);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigateHistory('up');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigateHistory('down');
    }
  };

  return (
    <div className="h-full bg-black text-green-300 font-mono text-sm flex flex-col">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-1">
        {history.map((entry) => (
          <div key={entry.id} className={entry.type === 'input' ? 'text-green-200' : 'text-green-300'}>
            {entry.text}
          </div>
        ))}
      </div>
      <form onSubmit={handleCommandSubmit} className="border-t border-green-900 bg-black p-3 flex items-center">
        <span className="text-green-200 mr-2">{DEFAULT_PROMPT}</span>
        <input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent text-green-200 outline-none"
          spellCheck={false}
          autoComplete="off"
        />
      </form>
    </div>
  );
};

export default CommandPromptContent;